import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FrameworkListingLibProvider } from 'framework-listing-lib';
import useFetchCrmObjectType from 'framework-listing-lib/hooks/useFetchCrmObjectType';
import { NEQ } from 'framework-listing-lib/utils/filters';
import { useCaptureError } from '../../../hooks/useCaptureError';
import { useDebounce } from '../../../hooks/useDebounce';
import { useInstallPermissions } from '../../../contexts/InstallPermissionsContext';
import { useFetchUsersWithInstallAccess } from '../../../hooks/queries/useFetchUsersWithInstallAccess';
import { UserList } from '../UserList';
import { MAX_VISIBLE_PAGE_BUTTONS } from '../../../constants/paginator';
import { UserSearchEmptyState } from '../../UserSearchEmptyState';
import { useTracking } from '../../../contexts/TrackingContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const USER_OBJECT_TYPE_ID = '0-115';
const SEARCHABLE_PROPERTY_NAME = 'hs_searchable_calculated_name';
// Mimics what is on the user and teams page
const DEFAULT_FILTERS = [NEQ('hs_hubspot_jita_user', 'true'), NEQ('hs_is_bot', 'true'), NEQ('hs_is_mam_user', 'true'), NEQ('hs_is_app_service_account', 'true'), NEQ('hs_is_work_agent', 'true')];
const USER_COUNT_PER_PAGE = 25;
function SpecificUsersAndTeamsInner({
  data,
  currentPage,
  onPageChange
}) {
  const pageCount = Math.ceil(data.total / USER_COUNT_PER_PAGE);
  const showPaginator = data.total > USER_COUNT_PER_PAGE;
  const handlePaginatorChange = event => {
    onPageChange(event.target.value);
  };
  const {
    results
  } = data;
  if (results.length === 0) {
    return /*#__PURE__*/_jsx(UserSearchEmptyState, {});
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UserList, {
      results: results
    }), showPaginator && /*#__PURE__*/_jsx(Paginator, {
      page: currentPage,
      pageCount: pageCount,
      onPageChange: handlePaginatorChange,
      marginBlockStart: "400",
      maxVisiblePageButtons: MAX_VISIBLE_PAGE_BUTTONS
    })]
  });
}
function useUserSelectionCounts(totalSelectableUsers) {
  const {
    superAdmins = {},
    integrationsManagementWriteUsers = {}
  } = useFetchUsersWithInstallAccess();
  const {
    initialUserIds,
    updatedUsers
  } = useInstallPermissions();
  return useMemo(() => {
    // Count selected users from initial and updated selections
    let selected = 0;

    // Count initially selected users (excluding super admins and integrations-management-write users)
    initialUserIds.forEach(userId => {
      const isStillSelected = userId in updatedUsers ? updatedUsers[userId] : true;
      const isSuperAdmin = !!superAdmins[userId];
      const isIntegrationsManagementWriteUser = !!integrationsManagementWriteUsers[userId];
      if (isStillSelected && !isSuperAdmin && !isIntegrationsManagementWriteUser) {
        selected++;
      }
    });

    // Add newly selected users (not in initialUserIds)
    Object.entries(updatedUsers).forEach(([userIdStr, isSelected]) => {
      const userId = Number(userIdStr);
      if (isSelected && !initialUserIds.has(userId)) {
        selected++;
      }
    });

    // Calculate total selectable users (all users minus super admins and integrations-management-write users)
    const superAdminCount = Object.keys(superAdmins).length;
    const integrationsManagementWriteUserCount = Object.keys(integrationsManagementWriteUsers).length;
    const totalSelectable = totalSelectableUsers ? totalSelectableUsers - superAdminCount - integrationsManagementWriteUserCount : 0;
    return {
      selectedCount: selected,
      filteredTotalCount: totalSelectable
    };
  }, [initialUserIds, updatedUsers, superAdmins, integrationsManagementWriteUsers, totalSelectableUsers]);
}
function SpecificUsersAndTeamsWithData({
  currentPage,
  onPageChange,
  onTotalLoad
}) {
  const {
    data,
    loading,
    error
  } = useFetchCrmObjectType();
  const hasLoadedTotalRef = useRef(false);
  useCaptureError(error, {
    message: 'Error fetching users and teams'
  });

  // Capture initial total only once
  useEffect(() => {
    if (data && !hasLoadedTotalRef.current) {
      onTotalLoad(data.total);
      hasLoadedTotalRef.current = true;
    }
  }, [data, onTotalLoad]);
  if (error) {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.errorBoundary.default.title"
      }),
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.installPermissions.error"
      })
    });
  }
  if (loading || !data) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      marginBlockStart: "400",
      justifyContent: "center"
    });
  }
  return /*#__PURE__*/_jsx(SpecificUsersAndTeamsInner, {
    data: data,
    currentPage: currentPage,
    onPageChange: onPageChange
  });
}
export function SpecificUsersAndTeams() {
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSelectableUsers, setTotalSelectableUsers] = useState(null);
  const debouncedQuery = useDebounce(searchInput, 250);
  const {
    tracker
  } = useTracking();
  const handleSearchChange = event => {
    setSearchInput(event.target.value);
    setCurrentPage(1);
  };
  const {
    selectedCount,
    filteredTotalCount
  } = useUserSelectionCounts(totalSelectableUsers);
  const offset = (currentPage - 1) * USER_COUNT_PER_PAGE;
  return /*#__PURE__*/_jsxs(FrameworkListingLibProvider, {
    objectType: USER_OBJECT_TYPE_ID,
    initialFilter: {
      sort: {
        property: SEARCHABLE_PROPERTY_NAME,
        order: 'ASC'
      },
      pagination: {
        count: USER_COUNT_PER_PAGE,
        offset
      },
      query: debouncedQuery
    },
    hiddenFilter: {
      filterGroups: [{
        filters: DEFAULT_FILTERS
      }]
    },
    trackerProps: {
      applicationId: 'app-governance-lib',
      tracker
    },
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "400",
      marginBlockEnd: "400",
      marginBlockStart: "600",
      children: [/*#__PURE__*/_jsxs(Text, {
        as: "h5",
        marginBlockEnd: "0",
        whiteSpace: "nowrap",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.installPermissions.options.specificUsers.usersSelected",
          options: {
            selectedCount,
            count: filteredTotalCount
          }
        }), filteredTotalCount > 0 && /*#__PURE__*/_jsx(Text, {
          as: "span",
          color: "text-core-subtle",
          marginInlineStart: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.installPermissions.options.specificUsers.usersSelectedCount",
            options: {
              selectedCount,
              count: filteredTotalCount
            }
          })
        })]
      }), /*#__PURE__*/_jsx(SearchInput, {
        maxInlineSize: "400px",
        marginInlineStart: "auto",
        value: searchInput,
        onChange: handleSearchChange,
        placeholder: I18n.text('app-governance-lib.installPermissions.options.specificUsers.search.placeholder')
      })]
    }), /*#__PURE__*/_jsx(SpecificUsersAndTeamsWithData, {
      currentPage: currentPage,
      onPageChange: setCurrentPage,
      onTotalLoad: setTotalSelectableUsers
    })]
  });
}